import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Flame, LogIn } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n";

export default function AuthGate({ lang, onToggleLang }) {
  const t = useT(lang);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password) {
      toast.error(t("errorAuthEmptyFields"));
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message || t("errorAuthGeneric"));
      return;
    }
    toast.success(t("successAuth"));
  };

  return (
    <div
      className="cockpit-bg min-h-screen w-full flex items-center justify-center px-4"
      data-testid="auth-gate"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#FF006E]/15 border border-[#FF006E]/40 flex items-center justify-center">
              <Flame size={18} strokeWidth={1.75} className="text-[#FF006E]" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold tracking-tight">
                {t("appTitle")}
              </h1>
              <p className="text-xs text-zinc-400">{t("appSubtitle")}</p>
            </div>
          </div>
          <div className="pill-group" data-testid="lang-toggle">
            <button
              type="button"
              className={`pill-btn ${lang === "sk" ? "active" : ""}`}
              onClick={() => onToggleLang("sk")}
              data-testid="lang-toggle-sk"
            >
              SK
            </button>
            <button
              type="button"
              className={`pill-btn ${lang === "en" ? "active" : ""}`}
              onClick={() => onToggleLang("en")}
              data-testid="lang-toggle-en"
            >
              EN
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="surface p-7" data-testid="login-form">
          <div className="mb-1 overline">{t("authOverline")}</div>
          <h2 className="font-display text-lg font-semibold mb-6">
            {t("authTitle")}
          </h2>

          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">
                {t("emailLabel")}
              </Label>
              <Input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="field-input bg-transparent"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">
                {t("passwordLabel")}
              </Label>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="field-input bg-transparent"
                data-testid="password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="cta-primary rounded-md px-5 py-3 text-sm font-semibold uppercase tracking-[0.14em] inline-flex items-center gap-2 w-full justify-center"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="typing">
                  <span />
                  <span />
                  <span />
                </span>
              ) : (
                <>
                  <LogIn size={16} strokeWidth={1.7} />
                  {t("loginBtn")}
                </>
              )}
            </button>
          </div>

          <p className="text-[11px] text-zinc-500 mt-5 leading-relaxed">
            {t("authHint")}
          </p>
          <p className="text-[12px] text-zinc-400 text-center mt-4">
            {t("authNoAccount")}{" "}
            <a
              href="/register"
              className="text-[#FF006E] hover:text-[#ff338b] font-medium"
              data-testid="auth-goto-register"
            >
              {t("registerSubmit")}
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}
